import { ListSubheader, Box, List, styled } from '@mui/material';
import { useLocation, matchPath } from 'react-router-dom';
import SidebarMenuItem from './SidebarMenuItem';
import menuItems, { MenuItem, developmentMenuItems, MenuItems } from './items';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useSettingsContext } from 'src/core/settings/useSettings';

function SidebarMenu() {
  const location = useLocation();
  const { t }: { t: any } = useTranslation();
  const { settings } = useSettingsContext();
  const allMenuItems = useMemo<MenuItems[]>(() => {
    const quicklinkMenuItems: MenuItem[] = [];
    settings?.Quicklinks?.forEach((linkName) => {
      const link = allLinks[linkName];
      if (link) {
        quicklinkMenuItems.push(link);
      }
    });
    const favMenuItem: MenuItems[] = quicklinkMenuItems.length
      ? [
        { heading: 'Quick Links', items: [...quicklinkMenuItems] }
      ]
      : [];
    return [...favMenuItem, ...menuItems, ...(settings?.DevelopmentMenu ? developmentMenuItems : [])];
  }, [settings?.Quicklinks, settings?.DevelopmentMenu]);

  return (
    <>
      {allMenuItems.map((section) => (
        <MenuWrapper key={section.heading}>
          <List component="div" subheader={<ListSubheader component="div" disableSticky>{t(section.heading)}</ListSubheader>}>{renderSidebarMenuItems({ items: section.items, path: location.pathname })}</List>
        </MenuWrapper>
      ))}
    </>
  );
}

const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    margin-bottom: ${theme.spacing(1.5)};
    padding: 0;

    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

    .MuiListSubheader-root {
      text-transform: uppercase;
      font-weight: bold;
      font-size: ${theme.typography.pxToRem(12)};
      color: ${theme.sidebar.menuItemIconColor};
      padding: ${theme.spacing(1, 3.5)};
      line-height: 1.4;
    }
`
);

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {

      .MuiListItem-root {
        padding: 1px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(5.5)};

          .MuiBadge-standard {
            background: ${theme.colors.primary.main};
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: ${theme.palette.primary.contrastText};
          }
        }
    
        .MuiButton-root {
          display: flex;
          color: ${theme.sidebar.menuItemColor};
          background-color: ${theme.sidebar.menuItemBg};
          width: 100%;
          border-radius: 0;
          justify-content: flex-start;
          padding: ${theme.spacing(1.2, 4)};

          &:after {
            content: '';
            position: absolute;
            height: 100%;
            right: 0;
            top: 0;
            width: 0;
            opacity: 0;
            transition: ${theme.transitions.create(['opacity', 'width'])};
            background: ${theme.sidebar.menuItemColorActive};
            border-top-left-radius: ${theme.general.borderRadius};
            border-bottom-left-radius: ${theme.general.borderRadius};
          }
    
          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color'])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            font-size: ${theme.typography.pxToRem(20)};
            margin-right: ${theme.spacing(1)};
            color: ${theme.sidebar.menuItemIconColor};
          }
          
          .MuiButton-endIcon {
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(20)};
          }

          &.active,
          &:hover {
            background-color: ${theme.sidebar.menuItemBgActive};
            color: ${theme.sidebar.menuItemColorActive};

            .MuiButton-startIcon,
            .MuiButton-endIcon {
                color: ${theme.sidebar.menuItemIconColorActive};
            }
          }

          &.active {
            &:after {
              width: 5px;
              opacity: 1;
            }
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(8)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 0;

            .MuiButton-root {
              padding: ${theme.spacing(0.7, 4, 0.7, 4.25)};

              .MuiBadge-root {
                right: ${theme.spacing(5.5)};
              }

              &:before {
                content: ' ';
                background: ${theme.sidebar.menuItemIconColorActive};
                opacity: 0;
                transition: ${theme.transitions.create(['transform', 'opacity'])};
                width: 6px;
                height: 6px;
                transform: scale(0);
                transform-origin: center;
                border-radius: 20px;
                margin-right: ${theme.spacing(1.8)};
              }

              &.active,
              &:hover {
                background-color: ${theme.sidebar.menuItemBg};

                &:before {
                  transform: scale(1);
                  opacity: 1;
                }

                &:after {
                  opacity: 0;
                }
              }
            }
          }
        }
      }
    }
`
);

const renderSidebarMenuItems = ({ items, path }: { items: MenuItem[]; path: string; }): JSX.Element => (
  <SubMenuWrapper>
    <List component="div">{items.reduce((ev, item) => reduceChildRoutes({ ev, item, path }), [])}</List>
  </SubMenuWrapper>
);

const reduceChildRoutes = ({ ev, path, item }: { ev: JSX.Element[]; path: string; item: MenuItem; }): Array<JSX.Element> => {
  const key = item.name;
  const exactMatch = item.link
    ? !!matchPath({ path: item.link, end: true }, path)
    : false;
  if (item.items) {
    const partialMatch = item.link
      ? !!matchPath({ path: item.link, end: false }, path)
      : false;
    ev.push(
      <SidebarMenuItem key={key} active={partialMatch} open={partialMatch} name={item.name} icon={item.icon} link={item.link} badge={item.badge} badgeTooltip={item.badgeTooltip}>{renderSidebarMenuItems({
        path,
        items: item.items
      })}</SidebarMenuItem>
    );
  } else {
    ev.push(<SidebarMenuItem key={key} active={exactMatch} name={item.name} link={item.link} badge={item.badge} badgeTooltip={item.badgeTooltip} icon={item.icon} />);
  }
  return ev;
};

const allLinks = {
  'Products': { name: 'Products', icon: FavoriteIcon, link: '/products/products', },
  'Create Product': { name: 'Create Product', icon: FavoriteIcon, link: '/products/products/create', },
  'Sales Items': { name: 'Sales Items', icon: FavoriteIcon, link: '/products/sales-items', },
  'Create Sales Item': { name: 'Create Sales Item', icon: FavoriteIcon, link: '/products/sales-items/create', },
  'Orders': { name: 'Orders', icon: FavoriteIcon, link: '/orders/orders', },
  'Purchase Orders': { name: 'Purchase Orders', icon: FavoriteIcon, link: '/purchasing/purchase-order', },
  'Create Purchase Order': { name: 'Create Purchase Order', icon: FavoriteIcon, link: '/purchasing/purchase-order/create', },
};

export default SidebarMenu;
